import React, { useEffect, useState } from 'react';
import 'bootstrap-select';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import LoadingSpinner from 'component/LoadingSpinner';
import { BaseResponse } from 'domain/payload/response/BaseResponseDto';
import { ProjectUserListResp } from 'domain/payload/response/clientTransmora/projectUser/ProjectUserListResp';
import ProjectUserService from 'service/ClientTransmoraService/ProjectUserService';
import { hasProjectUserAccess } from 'helpers/hasPrivilege';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';
import ProjectUserDetailModal from './ProjectUserDetailModal';

const ProjectUser: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const [data, setData] = useState<ProjectUserListResp[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const canCreate = hasProjectUserAccess(profile, 'create');
  const canUpdate = hasProjectUserAccess(profile, 'update');
  const canDelete = hasProjectUserAccess(profile, 'delete');

  const projectUserService = ProjectUserService();

  const fetchData = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const res: BaseResponse<ProjectUserListResp[]> = await projectUserService.projectUserList(
        token,
        searchTerm,
        currentPage,
        pageSize
      );
      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        typeof err === 'string'
          ? err
          : err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data Client Project User';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData();
  };

  const handleDelete = async (id: string) => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (!window.confirm('Apakah Anda yakin ingin menghapus Client Project User ini?')) return;

    try {
      setLoading(true);
      await projectUserService.projectUserDelete(token, id);
      window.alert('Client Project User berhasil dinonaktifkan');
      fetchData();
    } catch (err: any) {
      console.error('Error deleting Client Project User:', err);
      const errorMessage =
        typeof err === 'string'
          ? err
          : err.response?.data?.message || err.message || 'Terjadi kesalahan saat menghapus Client Project User';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (id: string) => {
    setSelectedUserId(id);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedUserId(null);
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Project User</h5>
            <div>
              <p>TransMore Client Project User</p>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="p-4" style={{ background: 'white' }}>
            <div className="mb-3 d-flex flex-nowrap gap-2 overflow-auto text-nowrap">
              <form className="d-flex" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: 160 }}
                />
                <button type="submit" className="px-4 btn btn-primary">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <div className="d-flex justify-content-end" style={{ flex: 1 }}>
                {canCreate && (
                  <Link to="/client-project-user/0" className="float-end text-decoration-none tm-text-primary">
                    <button className="btn btn-primary">
                      <span className="me-2">Add User</span>
                      <i className="fa-solid fa-plus"></i>
                    </button>
                  </Link>
                )}
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-condensed table-hover align-middle">
                <thead className="table-primary">
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Client</th>
                    <th>Roles</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading ? (
                    data.length > 0 ? (
                      data.map((item) => (
                        <tr key={item.id}>
                          <td>{item.id}</td>
                          <td className="fw-semibold">{item.name}</td>
                          <td>{item.email}</td>
                          <td>{item.phone || '-'}</td>
                          <td>{item.clientName || item.client || '-'}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {item.roles && item.roles.length > 0 ? (
                                item.roles.map((r: any) => (
                                  <span key={r.id} className="badge bg-light text-dark border">
                                    {r.name}
                                  </span>
                                ))
                              ) : (
                                '-'
                              )}
                            </div>
                          </td>
                          <td>{item.createdAt || '-'}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <button
                                className="border-0 btn btn-outline-info btn-small-compact d-flex align-items-center justify-content-center"
                                onClick={() => handleViewDetail(item.id)}
                                title="View Details"
                                style={{ width: '32px', height: '32px' }}
                              >
                                <i className="fa-solid fa-eye"></i>
                              </button>

                              {canUpdate && (
                                <Link to={'/client-project-user/' + item.id}>
                                  <button
                                    className="border-0 btn btn-outline-warning btn-small-compact ms-2 d-flex align-items-center justify-content-center"
                                    title="Edit"
                                    style={{ width: '32px', height: '32px' }}
                                  >
                                    <i className="fa-solid fa-edit"></i>
                                  </button>
                                </Link>
                              )}
                              {canDelete && (
                                <button
                                  className="border-0 btn btn-outline-danger btn-small-compact ms-2 d-flex align-items-center justify-content-center"
                                  onClick={() => handleDelete(item.id)}
                                  title="Delete"
                                  style={{ width: '32px', height: '32px' }}
                                >
                                  <i className="fa-solid fa-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="text-center py-4 text-muted">
                          Tidak ada data
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 d-flex justify-content-end align-items-center">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {totalRecords === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="mb-0 pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>

                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>
                        {currentPage}
                      </span>
                    </li>

                    <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProjectUserDetailModal
        show={showDetailModal}
        onClose={handleCloseModal}
        userId={selectedUserId}
        token={profile?.data?.token}
      />
    </div>
  );
};

export default ProjectUser;